import { IPlacerAssistantFacade, PlacerAssistantFacade } from './PlacerAssistantFacade';
import { PlacerAssistant } from '../model/PlacerAssistant';
import { IHttpClient, IServerSentEventsConsumerFactory } from '../../../infra';
import { PlacerAssistantClient } from './PlacerAssistantClient';
import { PlacerAssistantService } from './PlacerAssistantService';
import { PlacerAssistantFactory } from '../model/PlacerAssistantFactory';
import { AbortControllerFactory } from '../../../infra/httpClient/AbortControllerFactory';
import { ConversationsLoaderFactory } from './ConversationsLoaderFactory';
import {
    IRunLifecycleManagerFactory,
    RunLifecycleManagerFactory,
} from './RunLifecycleManagerFactory';
import { ContainerFactory, IContainerFactory } from '../model/containers/ContainerFactory';
import {
    ContainerReactRendererFactory,
    IContainerReactRendererFactory,
} from '../view/containers/ContainerReactRendererFactory';
import {
    IResourceReactRendererFactory,
    ResourceReactRendererFactory,
} from 'features/pi/view/resource/ResourceReactRenderFactory';
import { ResourceFactory } from 'features/pi/model/resources/ResourceFactory';

declare global {
    interface Window {
        assistantFacade: any;
    }
}

export interface IPlacerAssistantApp {
    facade: IPlacerAssistantFacade;
    containerRendererFactory: IContainerReactRendererFactory;
    resourceFactory: IResourceReactRendererFactory;
}

export function createPlacerAssistantApp(
    httpClient: IHttpClient,
    serverSentEventsFactory: IServerSentEventsConsumerFactory,
): IPlacerAssistantApp {
    const placerAssistantClient = new PlacerAssistantClient(httpClient);

    const containerFactory: IContainerFactory = new ContainerFactory();
    const placerAssistantFactory = new PlacerAssistantFactory(
        containerFactory,
        new ResourceFactory(),
    );
    const abortControllerFactory = new AbortControllerFactory();
    const placerAssistantService = new PlacerAssistantService(
        placerAssistantClient,
        placerAssistantFactory,
        {
            supportsScreenshot: true,
        },
    );
    const conversationsLoaderFactory = new ConversationsLoaderFactory(placerAssistantService);

    const runsLifecycleFactory: IRunLifecycleManagerFactory = new RunLifecycleManagerFactory(
        placerAssistantFactory,
        serverSentEventsFactory,
        placerAssistantService,
    );

    const model = new PlacerAssistant();
    const facade = new PlacerAssistantFacade(model, {
        placerAssistantService,
        abortControllerFactory,
        placerAssistantFactory,
        conversationsLoaderFactory,
        runsLifecycleFactory,
    });

    const containerRendererFactory: IContainerReactRendererFactory =
        new ContainerReactRendererFactory();

    const resourceFactory: IResourceReactRendererFactory = new ResourceReactRendererFactory();

    const result: IPlacerAssistantApp = {
        facade,
        containerRendererFactory,
        resourceFactory,
    };

    window.assistantFacade = { facade, placerAssistantClient };

    return result;
}
